import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { ShieldAlert, Check, X } from 'lucide-react';

import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { getCustomerCountry } from '../../components/CountrySelector';
import { getTCKimlikStatus, getNationalityGateConfig } from '../../components/TCKimlikDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * TCKimlikUserCard — Dashboard subcomponent for Turkish residents to manage
 * their TC Kimlik card status. Shows only when nationality gate is enabled
 * and user is in trigger country (or has a status set).
 *
 * Extracted from App.js (Fase 2) — pure relocation, no logic changes.
 */
export const TCKimlikUserCard = ({ user, token, onUpdate }) => {
  const { t, i18n } = useTranslation();
  const [gate, setGate] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getNationalityGateConfig().then(setGate).catch(() => {});
  }, []);

  const triggerCountry = gate?.trigger_country || 'TR';
  const fsCountry = getCustomerCountry();
  const status = user?.tc_kimlik_status || getTCKimlikStatus();

  // Only render if gate is enabled AND (user selected trigger country OR already has a status)
  if (!gate?.enabled) return null;
  if (fsCountry !== triggerCountry && !status) return null;

  const lang = (i18n.language || 'en').split('-')[0];
  const texts = (gate.popup_text_i18n || {})[lang] || (gate.popup_text_i18n || {}).en || {};

  const update = async (newValue) => {
    setSaving(true);
    try {
      await axios.post(
        `${API}/user/tc-kimlik-status`,
        { tc_kimlik_status: newValue },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Also persist in cookie for consistency with anonymous flow
      const d = new Date();
      d.setTime(d.getTime() + 365 * 86400000);
      document.cookie = `fs_tc_kimlik=${newValue};expires=${d.toUTCString()};path=/;SameSite=Lax`;
      onUpdate?.(newValue);
      toast.success(t('dashboard.tcKimlikUpdated', 'Status updated'));
    } catch (e) {
      toast.error(t('common.saveError', 'Failed to save'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="p-6 rounded-2xl mt-6" data-testid="tc-kimlik-user-card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-bold flex items-center gap-2">
          <ShieldAlert className="w-6 h-6 text-amber-600" />
          {texts.title || t('dashboard.tcKimlikTitle', 'Turkish Nationality Status')}
        </h3>
        {status && (
          <Badge variant={status === 'yes' ? 'default' : 'outline'} className={status === 'yes' ? 'bg-green-600' : 'border-red-300 text-red-600'}>
            {status === 'yes' ? (texts.yes || 'Yes') : (texts.no || 'No')}
          </Badge>
        )}
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        {texts.subtitle || 'Manage your TC Kimlik status. If you obtain a TC Kimlik card, update it here to unlock bookings in Turkey.'}
      </p>
      <div className="flex flex-col sm:flex-row gap-3">
        <Button
          onClick={() => update('yes')}
          disabled={saving || status === 'yes'}
          className="flex-1 h-11 rounded-full bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
          data-testid="tc-kimlik-set-yes"
        >
          <Check className="w-4 h-4 mr-2" />
          {texts.yes || 'I have a TC Kimlik card'}
        </Button>
        <Button
          onClick={() => update('no')}
          disabled={saving || status === 'no'}
          variant="outline"
          className="flex-1 h-11 rounded-full border-red-300 text-red-600 hover:bg-red-50 dark:hover:bg-red-950 disabled:opacity-50"
          data-testid="tc-kimlik-set-no"
        >
          <X className="w-4 h-4 mr-2" />
          {texts.no || "I don't have one"}
        </Button>
      </div>
    </Card>
  );
};

export default TCKimlikUserCard;
